/**
 * 알림 관련 데이터를 관리하는 컴포저블
 * 알림 목록 조회, 읽지 않은 알림 조회, 읽음 처리 기능을 제공합니다.
 */

import type {
  NotificationHistoryResponse,
  PageRequest,
  PageResponseNotificationHistoryResponse,
  UnreadNotificationHistoryResponse,
} from '@template/api';
import type { NotificationCategory } from '@/constant/notificationTypes';
import { memberService, notificationService } from '@/services/api';
import { ref, computed } from 'vue';

/**
 * 실제 API 응답의 알림 아이템 타입
 */
export interface NotificationListItem extends NotificationHistoryResponse {
  id: number;
  subject?: string;
  contents?: string;
  sendAt?: string;
  checked: boolean;
  webLink?: string;
  appLink?: string;
}

/**
 * 읽지 않은 알림 아이템 타입
 */
export interface UnreadNotificationListItem extends UnreadNotificationHistoryResponse {
  id: number;
  subject?: string;
  contents?: string;
  sendAt?: string;
  webLink?: string;
  appLink?: string;
}

export function useNotification() {
  // 로컬 상태
  const loading = ref(false);
  const error = ref<string | null>(null);

  // 알림 목록 데이터
  const notifications = ref<NotificationListItem[]>([]);
  const totalCount = ref(0);
  const hasNext = ref(false);
  const isLast = ref(false);

  // 읽지 않은 알림 목록 데이터
  const unreadNotifications = ref<UnreadNotificationListItem[]>([]);
  const unreadTotalCount = ref(0);
  const unreadHasNext = ref(false);
  const unreadIsLast = ref(false);

  // 페이지네이션 파라미터
  const pageableParams = ref<PageRequest>({
    page: 0,
    size: 20,
  });

  // 읽지 않은 알림 페이지네이션 파라미터
  const unreadPageableParams = ref<PageRequest>({
    page: 0,
    size: 20,
  });

  // 선택된 카테고리
  const selectedCategory = ref<NotificationCategory | undefined>(undefined);

  /**
   * 알림 내역 목록을 조회합니다.
   * 페이지네이션과 카테고리 필터를 적용하여 조회합니다.
   * @param pageRequest - 페이지네이션 정보 (page, size, sort)
   * @param category - 알림 카테고리 (선택사항)
   */
  const loadNotifications = async (
    pageRequest?: Partial<PageRequest>,
    category?: NotificationCategory
  ) => {
    try {
      loading.value = true;
      error.value = null;

      // 페이지네이션 정보 병합
      const mergedPageable: PageRequest = {
        ...pageableParams.value,
        ...pageRequest,
      };

      const response = await notificationService.getNotificationHistories({
        pageRequest: mergedPageable,
        category,
      });

      if (response?.data) {
        const apiResponse = response.data as PageResponseNotificationHistoryResponse;
        const contents = (apiResponse.contents || []) as NotificationListItem[];

        // 페이지가 0이면 새로 시작, 아니면 기존 목록에 추가
        if (mergedPageable.page === 0) {
          notifications.value = contents;
        } else {
          notifications.value = [...notifications.value, ...contents];
        }

        hasNext.value = !apiResponse.isLast;
        isLast.value = apiResponse.isLast;
        totalCount.value = apiResponse.totalElements || notifications.value.length;

        // 페이지네이션 파라미터 업데이트
        pageableParams.value = mergedPageable;
        selectedCategory.value = category;
      } else {
        notifications.value = [];
        totalCount.value = 0;
        hasNext.value = false;
        isLast.value = true;
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : '알림 목록을 불러오는데 실패했습니다.';
      error.value = errorMessage;
      notifications.value = [];
      totalCount.value = 0;
      hasNext.value = false;
      isLast.value = true;
      console.error('알림 목록 로드 실패:', err);
    } finally {
      loading.value = false;
    }
  };

  /**
   * 읽지 않은 알림 목록을 조회합니다.
   * 페이지네이션을 적용하여 조회합니다.
   * @param pageRequest - 페이지네이션 정보 (page, size, sort)
   */
  const loadUnreadNotifications = async (pageRequest?: Partial<PageRequest>) => {
    try {
      loading.value = true;
      error.value = null;

      // 페이지네이션 정보 병합
      const mergedPageable: PageRequest = {
        ...unreadPageableParams.value,
        ...pageRequest,
      };

      const response = await notificationService.getUnreadNotificationHistories(mergedPageable);

      if (response?.data) {
        const apiResponse = response.data as PageResponseNotificationHistoryResponse;
        const contents = (apiResponse.contents || []) as UnreadNotificationListItem[];

        // 페이지가 0이면 새로 시작, 아니면 기존 목록에 추가
        if (mergedPageable.page === 0) {
          unreadNotifications.value = contents;
        } else {
          unreadNotifications.value = [...unreadNotifications.value, ...contents];
        }

        unreadHasNext.value = !apiResponse.isLast;
        unreadIsLast.value = apiResponse.isLast;
        unreadTotalCount.value = apiResponse.totalElements || unreadNotifications.value.length;

        // 페이지네이션 파라미터 업데이트
        unreadPageableParams.value = mergedPageable;
      } else {
        unreadNotifications.value = [];
        unreadTotalCount.value = 0;
        unreadHasNext.value = false;
        unreadIsLast.value = true;
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : '읽지 않은 알림 목록을 불러오는데 실패했습니다.';
      error.value = errorMessage;
      unreadNotifications.value = [];
      unreadTotalCount.value = 0;
      unreadHasNext.value = false;
      unreadIsLast.value = true;
      console.error('읽지 않은 알림 목록 로드 실패:', err);
    } finally {
      loading.value = false;
    }
  };

  /**
   * 카테고리로 알림을 필터링합니다.
   * @param category - 카테고리 타입
   */
  const filterByCategory = async (category?: NotificationCategory) => {
    await loadNotifications({ page: 0 }, category);
  };

  /**
   * 다음 페이지를 로드합니다.
   */
  const loadNextPage = async () => {
    if (loading.value || !hasNext.value) {
      return;
    }

    const currentPage = pageableParams.value.page || 0;
    await loadNotifications({ page: currentPage + 1 }, selectedCategory.value);
  };

  /**
   * 읽지 않은 알림의 다음 페이지를 로드합니다.
   */
  const loadUnreadNextPage = async () => {
    if (loading.value || !unreadHasNext.value) {
      return;
    }

    const currentPage = unreadPageableParams.value.page || 0;
    await loadUnreadNotifications({ page: currentPage + 1 });
  };

  /**
   * 특정 알림을 읽음 처리합니다.
   * @param notificationId - 알림 ID
   */
  const markAsChecked = async (notificationId: number) => {
    try {
      error.value = null;

      await notificationService.markAsChecked(notificationId);

      // 로컬 상태 업데이트: 해당 알림을 읽음 처리
      const notification = notifications.value.find((n) => n.id === notificationId);
      if (notification) {
        notification.checked = true;
      }

      // 읽지 않은 알림 목록에서 제거
      unreadNotifications.value = unreadNotifications.value.filter((n) => n.id !== notificationId);
      if (unreadTotalCount.value > 0) {
        unreadTotalCount.value--;
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '알림 읽음 처리에 실패했습니다.';
      error.value = errorMessage;
      console.error('알림 읽음 처리 실패:', err);
      throw err;
    }
  };

  /**
   * 읽지 않은 알림 전체를 읽음 처리합니다.
   */
  const markAllAsChecked = async () => {
    try {
      error.value = null;

      await notificationService.markAllAsChecked();

      // 로컬 상태 업데이트: 모든 알림을 읽음 처리
      notifications.value.forEach((notification) => {
        notification.checked = true;
      });

      // 읽지 않은 알림 목록 초기화
      unreadNotifications.value = [];
      unreadTotalCount.value = 0;
      unreadHasNext.value = false;
      unreadIsLast.value = true;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : '알림 전체 읽음 처리에 실패했습니다.';
      error.value = errorMessage;
      console.error('알림 전체 읽음 처리 실패:', err);
      throw err;
    }
  };

  /**
   * 특정 알림을 삭제합니다.
   * 실제로는 삭제되지 않고 조회 목록에서만 제외됩니다.
   * @param notificationId - 알림 ID
   */
  const deleteNotification = async (notificationId: number) => {
    try {
      error.value = null;

      await notificationService.deleteNotification(notificationId);

      // 로컬 상태 업데이트: 알림 목록에서 제거
      notifications.value = notifications.value.filter((n) => n.id !== notificationId);
      if (totalCount.value > 0) {
        totalCount.value--;
      }

      // 읽지 않은 알림 목록에서도 제거
      const wasUnread = unreadNotifications.value.some((n) => n.id === notificationId);
      if (wasUnread) {
        unreadNotifications.value = unreadNotifications.value.filter(
          (n) => n.id !== notificationId
        );
        if (unreadTotalCount.value > 0) {
          unreadTotalCount.value--;
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '알림 삭제에 실패했습니다.';
      error.value = errorMessage;
      console.error('알림 삭제 실패:', err);
      throw err;
    }
  };

  /**
   * 데이터를 새로고침합니다.
   */
  const refreshNotifications = async () => {
    await loadNotifications({ page: 0 }, selectedCategory.value);
  };

  /**
   * 읽지 않은 알림 데이터를 새로고침합니다.
   */
  const refreshUnreadNotifications = async () => {
    await loadUnreadNotifications({ page: 0 });
  };

  /**
   * 검색 조건을 초기화합니다.
   */
  const resetParams = () => {
    selectedCategory.value = undefined;
    pageableParams.value = {
      page: 0,
      size: 20,
    };
    unreadPageableParams.value = {
      page: 0,
      size: 20,
    };
  };

  /**
   * 마케팅 동의 여부를 변경합니다.
   * 회원의 마케팅(광고성 정보) 수신 동의 여부를 변경합니다.
   * @param agreed - 마케팅 동의 여부
   */
  const updateMarketingAgreement = async (agreed: boolean) => {
    try {
      error.value = null;

      await memberService.updateMarketingAgreement({ agreed });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '마케팅 동의 변경에 실패했습니다.';
      error.value = errorMessage;
      console.error('마케팅 동의 변경 실패:', err);
      throw err;
    }
  };

  // 계산된 속성들
  const isEmpty = computed(() => notifications.value.length === 0 && !loading.value);
  const hasNotifications = computed(() => notifications.value.length > 0);
  const hasMore = computed(() => hasNext.value);

  const unreadIsEmpty = computed(() => unreadNotifications.value.length === 0 && !loading.value);
  const hasUnreadNotifications = computed(() => unreadNotifications.value.length > 0);
  const hasUnreadMore = computed(() => unreadHasNext.value);

  return {
    // 상태
    loading,
    error,
    notifications,
    totalCount,
    hasNext,
    isLast,
    unreadNotifications,
    unreadTotalCount,
    unreadHasNext,
    unreadIsLast,
    pageableParams,
    unreadPageableParams,
    selectedCategory,

    // 계산된 속성
    isEmpty,
    hasNotifications,
    hasMore,
    unreadIsEmpty,
    hasUnreadNotifications,
    hasUnreadMore,

    // 메서드
    loadNotifications,
    loadUnreadNotifications,
    filterByCategory,
    loadNextPage,
    loadUnreadNextPage,
    markAsChecked,
    markAllAsChecked,
    deleteNotification,
    refreshNotifications,
    refreshUnreadNotifications,
    resetParams,
    updateMarketingAgreement,
  };
}
